/**
 * Design tokens — single source of truth pentru ambele teme.
 *
 * Folosire: prin CSS variables (`var(--bg-canvas)`, `var(--fg-primary)`, etc.)
 * setate de `ThemeContext` pe `<html>` în funcție de tema activă.
 *
 * Brand colors sunt identice în ambele teme (violet/teal/blue/status).
 * Tokens semantice (`bgCanvas`, `fgPrimary`, etc.) au valori diferite per temă.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Brand — identice în ambele teme
// ─────────────────────────────────────────────────────────────────────────────
export const brand = {
  violet: '#7c3aed',       // accent primar (folosit dominant în UI)
  violetSoft: '#8b5cf6',   // hover/glow
  violetLight: '#a78bfa',  // text accent on dark surfaces
  violetDeep: '#6d28d9',   // hover pe light theme (mai întunecat)
  teal: '#00BFA6',         // accent secundar
  blue: '#0052CC',         // accent terțiar
  blueDeep: '#003D99',
  success: '#28A745',
  warning: '#FF8C42',
  danger: '#E63946',
  // Status soft pentru text/border în dark
  successText: '#4ade80',
  warningText: '#fbbf24',
  dangerText: '#f87171',
  // Status soft pentru text/border în light
  successTextLight: '#15803d',
  warningTextLight: '#a16207',
  dangerTextLight: '#dc2626',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Token shape (TypeScript-strict, ambele teme trebuie să aibă aceleași chei)
// ─────────────────────────────────────────────────────────────────────────────
export interface ThemeTokens {
  // Backgrounds (de la jos la sus pe Z)
  bgCanvas: string;       // pagină
  bgSurface: string;      // card / input default
  bgElevated: string;     // modal / dropdown / popover
  bgHover: string;        // hover state pe surface
  bgActive: string;       // active state pe surface
  bgOverlay: string;      // backdrop pentru modal
  bgInverted: string;     // chip/badge cu contrast invers

  // Foreground (5 nivele de hierarchy)
  fgPrimary: string;      // titlu, body principal
  fgSecondary: string;    // body secundar, descrieri
  fgMuted: string;        // labels, captions
  fgSubtle: string;       // hints, durations, valori secundare
  fgDisabled: string;
  fgOnAccent: string;     // text pe gradient violet/teal (de obicei alb)
  fgOnInverted: string;   // text pe bgInverted

  // Borders
  borderSubtle: string;   // dividers
  borderDefault: string;  // input, card
  borderStrong: string;   // emphasis
  borderFocus: string;    // focus ring

  // Accent (violet) — semantic
  accent: string;
  accentHover: string;
  accentSubtle: string;   // bg subtle pe accent (rgba violet)
  accentBorder: string;
  accentText: string;     // text accent (mai bine vizibil decât accent pe surface)

  // Status backgrounds (foarte subtile, pentru badge/banner)
  successBg: string;
  successBorder: string;
  warningBg: string;
  warningBorder: string;
  dangerBg: string;
  dangerBorder: string;

  // Shadow (folosite ca string-uri pentru box-shadow)
  shadowSm: string;
  shadowMd: string;
  shadowLg: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Dark theme
// ─────────────────────────────────────────────────────────────────────────────
export const darkTokens: ThemeTokens = {
  bgCanvas: '#050510',
  bgSurface: 'rgba(255, 255, 255, 0.03)',
  bgElevated: '#1a1a2e',
  bgHover: 'rgba(255, 255, 255, 0.06)',
  bgActive: 'rgba(255, 255, 255, 0.1)',
  bgOverlay: 'rgba(0, 0, 0, 0.85)',
  bgInverted: '#FFFFFF',

  fgPrimary: '#E8E8E8',
  fgSecondary: 'rgba(255, 255, 255, 0.7)',
  fgMuted: 'rgba(255, 255, 255, 0.5)',
  fgSubtle: 'rgba(255, 255, 255, 0.3)',
  fgDisabled: 'rgba(255, 255, 255, 0.2)',
  fgOnAccent: '#FFFFFF',
  fgOnInverted: '#1F2933',

  borderSubtle: 'rgba(255, 255, 255, 0.06)',
  borderDefault: 'rgba(255, 255, 255, 0.1)',
  borderStrong: 'rgba(255, 255, 255, 0.2)',
  borderFocus: brand.violetSoft,

  accent: brand.violet,
  accentHover: brand.violetSoft,
  accentSubtle: 'rgba(124, 58, 237, 0.12)',
  accentBorder: 'rgba(124, 58, 237, 0.3)',
  accentText: brand.violetLight,

  successBg: 'rgba(40, 167, 69, 0.12)',
  successBorder: 'rgba(40, 167, 69, 0.3)',
  warningBg: 'rgba(234, 179, 8, 0.1)',
  warningBorder: 'rgba(234, 179, 8, 0.3)',
  dangerBg: 'rgba(239, 68, 68, 0.1)',
  dangerBorder: 'rgba(239, 68, 68, 0.3)',

  shadowSm: '0 2px 4px rgba(0, 0, 0, 0.4)',
  shadowMd: '0 8px 24px rgba(0, 0, 0, 0.4)',
  shadowLg: '0 20px 60px rgba(0, 0, 0, 0.5)',
};

// ─────────────────────────────────────────────────────────────────────────────
// Light theme
// ─────────────────────────────────────────────────────────────────────────────
export const lightTokens: ThemeTokens = {
  bgCanvas: '#F5F7FA',
  bgSurface: '#FFFFFF',
  bgElevated: '#FFFFFF',
  bgHover: 'rgba(0, 0, 0, 0.04)',
  bgActive: 'rgba(0, 0, 0, 0.06)',
  bgOverlay: 'rgba(15, 23, 42, 0.5)',
  bgInverted: '#1F2933',

  fgPrimary: '#1F2933',
  fgSecondary: '#374151',
  fgMuted: '#6B7280',
  fgSubtle: '#9CA3AF',
  fgDisabled: '#D1D5DB',
  fgOnAccent: '#FFFFFF',
  fgOnInverted: '#FFFFFF',

  borderSubtle: '#E5E7EB',
  borderDefault: '#D1D5DB',
  borderStrong: '#9CA3AF',
  borderFocus: brand.violet,

  accent: brand.violet,
  accentHover: brand.violetDeep,
  accentSubtle: 'rgba(124, 58, 237, 0.08)',
  accentBorder: 'rgba(124, 58, 237, 0.25)',
  accentText: brand.violetDeep,

  successBg: 'rgba(34, 197, 94, 0.08)',
  successBorder: 'rgba(34, 197, 94, 0.25)',
  warningBg: '#fefce8',
  warningBorder: '#fde68a',
  dangerBg: '#fef2f2',
  dangerBorder: '#fecaca',

  shadowSm: '0 1px 2px rgba(15, 23, 42, 0.06)',
  shadowMd: '0 4px 12px rgba(15, 23, 42, 0.08)',
  shadowLg: '0 12px 32px rgba(15, 23, 42, 0.12)',
};

// ─────────────────────────────────────────────────────────────────────────────
// CSS variable mapping — chei snake-case-like pentru CSS, valoare = token key
// ─────────────────────────────────────────────────────────────────────────────
export const cssVarMap: Record<keyof ThemeTokens, string> = {
  bgCanvas: '--bg-canvas',
  bgSurface: '--bg-surface',
  bgElevated: '--bg-elevated',
  bgHover: '--bg-hover',
  bgActive: '--bg-active',
  bgOverlay: '--bg-overlay',
  bgInverted: '--bg-inverted',

  fgPrimary: '--fg-primary',
  fgSecondary: '--fg-secondary',
  fgMuted: '--fg-muted',
  fgSubtle: '--fg-subtle',
  fgDisabled: '--fg-disabled',
  fgOnAccent: '--fg-on-accent',
  fgOnInverted: '--fg-on-inverted',

  borderSubtle: '--border-subtle',
  borderDefault: '--border-default',
  borderStrong: '--border-strong',
  borderFocus: '--border-focus',

  accent: '--accent',
  accentHover: '--accent-hover',
  accentSubtle: '--accent-subtle',
  accentBorder: '--accent-border',
  accentText: '--accent-text',

  successBg: '--success-bg',
  successBorder: '--success-border',
  warningBg: '--warning-bg',
  warningBorder: '--warning-border',
  dangerBg: '--danger-bg',
  dangerBorder: '--danger-border',

  shadowSm: '--shadow-sm',
  shadowMd: '--shadow-md',
  shadowLg: '--shadow-lg',
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper styled-components: `t.bg.canvas` în loc de `var(--bg-canvas)`
// (opțional — codul poate folosi direct `var(--bg-canvas)` dacă preferă)
// ─────────────────────────────────────────────────────────────────────────────
export const t = {
  bg: {
    canvas: 'var(--bg-canvas)',
    surface: 'var(--bg-surface)',
    elevated: 'var(--bg-elevated)',
    hover: 'var(--bg-hover)',
    active: 'var(--bg-active)',
    overlay: 'var(--bg-overlay)',
    inverted: 'var(--bg-inverted)',
  },
  fg: {
    primary: 'var(--fg-primary)',
    secondary: 'var(--fg-secondary)',
    muted: 'var(--fg-muted)',
    subtle: 'var(--fg-subtle)',
    disabled: 'var(--fg-disabled)',
    onAccent: 'var(--fg-on-accent)',
    onInverted: 'var(--fg-on-inverted)',
  },
  border: {
    subtle: 'var(--border-subtle)',
    default: 'var(--border-default)',
    strong: 'var(--border-strong)',
    focus: 'var(--border-focus)',
  },
  accent: {
    base: 'var(--accent)',
    hover: 'var(--accent-hover)',
    subtle: 'var(--accent-subtle)',
    border: 'var(--accent-border)',
    text: 'var(--accent-text)',
  },
  status: {
    successBg: 'var(--success-bg)',
    successBorder: 'var(--success-border)',
    warningBg: 'var(--warning-bg)',
    warningBorder: 'var(--warning-border)',
    dangerBg: 'var(--danger-bg)',
    dangerBorder: 'var(--danger-border)',
  },
  shadow: {
    sm: 'var(--shadow-sm)',
    md: 'var(--shadow-md)',
    lg: 'var(--shadow-lg)',
  },
} as const;
